import React, { useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authClient } from './lib/auth-client';
import { Toaster } from '@/components/ui/toaster';
import Layout from './components/shared/Layout';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import JobList from './pages/jobs/JobList';
import JobDetail from './pages/jobs/JobDetail';
import JobForm from './pages/jobs/JobForm';
import InspectionReportEditor from './pages/inspections/InspectionReportEditor';
import ClientList from './pages/clients/ClientList';
import NewClient from './pages/clients/NewClient';
import ClientDetail from './pages/clients/ClientDetail';
import EditClient from './pages/clients/EditClient';
import LocationList from './pages/locations/LocationList';
import LocationDetail from './pages/locations/LocationDetail';
import WorkerList from './pages/workers/WorkerList';
import WorkerFormPage from './pages/workers/WorkerFormPage';
import Timesheets from './pages/timesheets/Timesheets';
import SettingsLayout from './pages/settings/SettingsLayout';
import BreakdownTemplates from './pages/settings/breakdown-templates/BreakdownTemplates';
import TemplateBreakdownEditPage from './pages/settings/breakdown-templates/TemplateBreakdownEditPage';
import RatesSettings from './pages/settings/rates/RatesSettings';
import CommonLineItems from './pages/settings/common-line-items/CommonLineItems';
import ProjectTypesSettings from './pages/settings/project-types/ProjectTypesSettings';
import LineItemCategoriesSettings from './pages/settings/line-item-categories/LineItemCategoriesSettings';
import SubItemCategoriesSettings from './pages/settings/sub-item-categories/SubItemCategoriesSettings';
import ExpenseCategoriesSettings from './pages/settings/expense-categories/ExpenseCategoriesSettings';
import CategoryDefaultsSettings from './pages/settings/category-defaults/CategoryDefaultsSettings';
import EquipmentSettings from './pages/settings/EquipmentSettings';
import CarpetPricingSettings from './pages/settings/CarpetPricingSettings';
import BillingSettings from './pages/settings/billing/BillingSettings';
import RecurringList from './pages/recurring/RecurringList';
import HelpPage from './pages/help/HelpPage';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import SharedFilesPage from './pages/share/SharedFilesPage';
import InvoiceWorkspace from './pages/invoices/InvoiceWorkspace';

function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  React.useEffect(() => {
    // Back and forward keep their offset: either a list restored it itself
    // (useScrollRestoration) or the browser restores document scroll. Only a
    // forward navigation is a new place, and new places start at the top.
    if (navigationType === 'POP') return;
    window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  return null;
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending, error } = authClient.useSession();
  const { pathname } = useLocation();
  const hadSessionRef = useRef(false);

  if (session) hadSessionRef.current = true;

  // Only show loading on initial load, not session revalidation
  if (isPending && !hadSessionRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error && !hadSessionRef.current) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-gray-600">Unable to connect to the server. Please try again.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Keyed on the pathname so navigating away clears a caught error; without
  // the key the boundary would hold its fallback across the route change and
  // the next page would never render.
  return (
    <Layout>
      <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
    </Layout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Client-facing share links. Outside ProtectedRoute and outside
              Layout: whoever opens this has no account and no app chrome to
              navigate. */}
          <Route path="/share/:token" element={<SharedFilesPage />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <JobList />
              </ProtectedRoute>
            }
          />

          {/* Receivables workspace — a sibling of /jobs, not a job sub-route. */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoiceWorkspace />
              </ProtectedRoute>
            }
          />

          {/* Job Routes */}
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <JobList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <ProtectedRoute>
                <JobForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute>
                <JobDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id/edit"
            element={
              <ProtectedRoute>
                <JobForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId/inspection-reports/:reportId"
            element={
              <ProtectedRoute>
                <InspectionReportEditor />
              </ProtectedRoute>
            }
          />

          {/* Recurring Reminders */}
          <Route
            path="/recurring"
            element={
              <ProtectedRoute>
                <RecurringList />
              </ProtectedRoute>
            }
          />

          {/* Client Routes */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ClientList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/new"
            element={
              <ProtectedRoute>
                <NewClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute>
                <ClientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id/edit"
            element={
              <ProtectedRoute>
                <EditClient />
              </ProtectedRoute>
            }
          />

          {/* Service Address Routes */}
          <Route
            path="/locations"
            element={
              <ProtectedRoute>
                <LocationList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations/:id"
            element={
              <ProtectedRoute>
                <LocationDetail />
              </ProtectedRoute>
            }
          />

          {/* Timesheets Routes */}
          <Route
            path="/timesheets"
            element={
              <ProtectedRoute>
                <Timesheets />
              </ProtectedRoute>
            }
          />

          {/* User Manual */}
          <Route
            path="/help/*"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Routes */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/settings/job-type-templates" replace />} />
            <Route path="job-type-templates" element={<BreakdownTemplates />} />
            <Route path="job-type-templates/:id/edit" element={<TemplateBreakdownEditPage />} />
            <Route path="workers" element={<WorkerList />} />
            <Route path="workers/new" element={<WorkerFormPage />} />
            <Route path="workers/:id/edit" element={<WorkerFormPage />} />
            <Route path="common-line-items" element={<CommonLineItems />} />
            <Route path="project-types" element={<ProjectTypesSettings />} />
            <Route path="line-item-categories" element={<LineItemCategoriesSettings />} />
            <Route path="sub-item-categories" element={<SubItemCategoriesSettings />} />
            <Route path="expense-categories" element={<ExpenseCategoriesSettings />} />
            <Route path="category-defaults" element={<CategoryDefaultsSettings />} />
            <Route path="rates" element={<RatesSettings />} />
            <Route path="equipment" element={<EquipmentSettings />} />
            <Route path="carpet-rates" element={<CarpetPricingSettings />} />
            <Route path="billing" element={<BillingSettings />} />
          </Route>
        </Routes>
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
